// Diagram trapezu ABCD podzielonego przez odcinek EC
// na równoległobok AECD i trójkąt EBC.
// Wszystkie współrzędne wyznaczane dynamicznie z kątów ABC i BCE.

import React from "react";
import { InlineMath } from "react-katex";

const DEG = Math.PI / 180;

function norm(vx, vy) {
  const len = Math.hypot(vx, vy) || 1;
  return { x: vx / len, y: vy / len };
}

export default function E8TrapezoidPartitionDiagram({ angleABC, angleBCE }) {
  const beta = angleABC * DEG;   // kąt przy B
  const alpha = (180 - angleABC - angleBCE) * DEG; // kąt DAB = CEB

  // Płótno SVG
  const W = 460, H = 220;
  const baseY = 182, topY = 68;
  const hPx = baseY - topY;

  // Wierzchołki podstawy
  const aX = 78, bX = 378;

  // C: z B pod kątem beta z BA (ku górze i w lewo)
  // kierunek BC w SVG (y w dół): (-cos β, -sin β)
  const LBC = hPx / Math.sin(beta);
  const cX = bX - LBC * Math.cos(beta);

  // D: z A pod kątem alpha z AB (ku górze i w prawo)
  // kierunek AD w SVG: (cos α, -sin α)
  const LAD = hPx / Math.sin(alpha);
  const dX = aX + LAD * Math.cos(alpha);

  // E: warunek równoległoboku AECD ⟹ E = A + C − D
  const eX = aX + cX - dX;

  const A = { x: aX, y: baseY };
  const B = { x: bX, y: baseY };
  const C = { x: cX, y: topY };
  const D = { x: dX, y: topY };
  const E = { x: eX, y: baseY };

  const LINE   = "currentColor";
  const ACCENT = "#f0ad3d";
  const FILL_P = "rgba(240,173,61,0.16)"; // równoległobok
  const FILL_T = "rgba(240,173,61,0.09)"; // trójkąt

  const pts = (ps) => ps.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // ── Łuk kąta ABC przy B ──────────────────────────────────────────────────
  const arcR = 30;
  // start: wzdłuż BA = (-1, 0)
  const abcS = { x: B.x - arcR, y: B.y };
  // end: wzdłuż BC = (-cos β, -sin β)
  const abcE = { x: B.x - arcR * Math.cos(beta), y: B.y - arcR * Math.sin(beta) };
  // sweep=1 (CW wizualnie) przesuwa od BA ku BC przez wnętrze trapezu ✓

  // Etykieta ∠ABC: bisektora kierunków BA i BC
  const abcBis = norm(-1 + (-Math.cos(beta)), 0 + (-Math.sin(beta)));
  const abcLbl = { x: B.x + (arcR + 19) * abcBis.x, y: B.y + (arcR + 19) * abcBis.y };

  // ── Łuk kąta BCE przy C ──────────────────────────────────────────────────
  // Kierunek CB z C: (cos β, sin β)  [ku dołowi i w prawo]
  // Kierunek CE z C: (-cos α, sin α) [ku dołowi i w lewo]
  const bceS = { x: C.x + arcR * Math.cos(beta), y: C.y + arcR * Math.sin(beta) };
  const bceE = { x: C.x - arcR * Math.cos(alpha), y: C.y + arcR * Math.sin(alpha) };
  // sweep=1 (CW) przesuwa od prawej ku lewej przez dół = wnętrze trójkąta EBC ✓

  // Etykieta ∠BCE: bisektora CB i CE
  const bceBis = norm(Math.cos(beta) + (-Math.cos(alpha)), Math.sin(beta) + Math.sin(alpha));
  const bceLbl = { x: C.x + (arcR + 19) * bceBis.x, y: C.y + (arcR + 19) * bceBis.y };

  // Pomocnik: etykieta kąta przez KaTeX (foreignObject jak w maturalnych)
  const ALabel = ({ x, y, value }) => {
    const w = 54, h = 26;
    return (
      <foreignObject x={x - w / 2} y={y - h / 2} width={w} height={h}>
        <div
          style={{
            color: ACCENT,
            fontSize: 14,
            fontWeight: 700,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <InlineMath math={`${value}^{\\circ}`} />
        </div>
      </foreignObject>
    );
  };

  // Pomocnik: litera wierzchołka
  const VLabel = ({ p, text, ox = 0, oy = 0 }) => (
    <text
      x={(p.x + ox).toFixed(1)}
      y={(p.y + oy).toFixed(1)}
      fill={LINE}
      fontSize="19"
      fontStyle="italic"
      fontFamily="serif"
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {text}
    </text>
  );

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "18px 0 20px" }}>
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Trapez ABCD podzielony przez EC na równoległobok AECD i trójkąt EBC"
        style={{ maxWidth: "100%", height: "auto" }}
      >
        {/* Wypełnienia */}
        <polygon points={pts([A, D, C, E])} fill={FILL_P} stroke="none" />
        <polygon points={pts([E, B, C])} fill={FILL_T} stroke="none" />

        {/* Obrys trapezu */}
        <polygon points={pts([A, B, C, D])} fill="none" stroke={LINE} strokeWidth="2.5" strokeLinejoin="round" />

        {/* Podstawa dolna (żeby E było widoczne między A i B) */}
        <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={LINE} strokeWidth="2.5" />

        {/* Odcinek EC — podział trapezu */}
        <line
          x1={E.x.toFixed(1)} y1={E.y.toFixed(1)}
          x2={C.x.toFixed(1)} y2={C.y.toFixed(1)}
          stroke={ACCENT} strokeWidth="2.5"
        />

        {/* Łuk kąta ABC przy B */}
        <path
          d={`M ${abcS.x.toFixed(1)} ${abcS.y.toFixed(1)} A ${arcR} ${arcR} 0 0 1 ${abcE.x.toFixed(1)} ${abcE.y.toFixed(1)}`}
          fill="none" stroke={ACCENT} strokeWidth="2"
        />
        <ALabel x={abcLbl.x} y={abcLbl.y} value={angleABC} />

        {/* Łuk kąta BCE przy C */}
        <path
          d={`M ${bceS.x.toFixed(1)} ${bceS.y.toFixed(1)} A ${arcR} ${arcR} 0 0 1 ${bceE.x.toFixed(1)} ${bceE.y.toFixed(1)}`}
          fill="none" stroke={ACCENT} strokeWidth="2"
        />
        <ALabel x={bceLbl.x} y={bceLbl.y} value={angleBCE} />

        {/* Etykiety wierzchołków */}
        <VLabel p={A} text="A" ox={-14} oy={14} />
        <VLabel p={B} text="B" ox={14}  oy={14} />
        <VLabel p={C} text="C" ox={14}  oy={-6} />
        <VLabel p={D} text="D" ox={-14} oy={-6} />
        <VLabel p={E} text="E" ox={0}   oy={18} />
      </svg>
    </div>
  );
}
